import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Logo from './Logo';
import { Ic } from '../utils/icons';

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [mobile, setMobile] = useState(false);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  const ref = useRef(null);
  const closeTimerRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 14);
    window.addEventListener('scroll', onScroll); onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setActiveCategory(null);
      }
    };
    document.addEventListener('click', onDoc);
    return () => {
      document.removeEventListener('click', onDoc);
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  const handleMouseEnter = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    setOpen(true);
  };

  const handleMouseLeave = () => {
    closeTimerRef.current = setTimeout(() => {
      setOpen(false);
      setActiveCategory(null);
    }, 200);
  };

  const toggleDropdown = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    setOpen((v) => {
      if (v) setActiveCategory(null);
      return !v;
    });
  };

  const links = [
    { label: 'HOME', href: '/#top' },
    { label: 'SOLUTIONS', href: '/#solutions' },
    { label: 'CAPABILITIES', href: '/#capabilities' },
    { label: 'BLOGS', href: '/blogs' },
  ];

  const handleNavClick = (e, href) => {
    const hash = href.split('#')[1];
    if (hash) {
      const el = document.getElementById(hash);
      if (el) {
        e.preventDefault();
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else if (hash === 'top' && window.location.pathname === '/') {
        e.preventDefault();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/85 backdrop-blur-xl border-b border-slate-200/80 shadow-sm' : 'border-b border-transparent'}`}>
      <nav className="max-w-[1240px] mx-auto px-5 sm:px-8 h-[68px] flex items-center justify-between">
        <Logo />

        <div className="hidden lg:flex items-center gap-1 text-[12.5px] font-600 tracking-[0.14em] text-mute">
          {links.map((l) => (
            <Link key={l.label} to={l.href} onClick={(e) => handleNavClick(e, l.href)}
               className="px-3.5 py-2 rounded-md hover:text-ink transition-colors relative group">
              {l.label}
              <span className="absolute left-3.5 right-3.5 -bottom-0.5 h-px scale-x-0 group-hover:scale-x-100 transition-transform origin-left" style={{background:'var(--lead)'}}></span>
            </Link>
          ))}

          {/* Products Dropdown (Hover + Click) */}
          <div
            className="relative"
            ref={ref}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <button
              onClick={toggleDropdown}
              aria-expanded={open}
              className={`px-3.5 py-2 rounded-md flex items-center gap-1.5 transition-colors ${open ? 'text-ink' : 'hover:text-ink'}`}
            >
              PRODUCTS
              {/* eslint-disable-next-line react/jsx-pascal-case */}
              <Ic.chevron className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
            </button>

            {/* Dropdown Menu: Shows simply the 2 options */}
            <div className={`menu-pop absolute left-0 mt-2 w-[240px] origin-top-left transition-all duration-200 z-50 ${open ? 'opacity-100 scale-100 pointer-events-auto' : 'opacity-0 scale-95 pointer-events-none'}`}>
              <div className="rounded-xl border border-slate-200/80 bg-white/95 backdrop-blur-xl p-2 shadow-xl shadow-slate-900/10 flex flex-col gap-1">

                {/* Option 1: E-Mobility Solution */}
                <div
                  className="relative group/emobility"
                  onMouseEnter={() => setActiveCategory('emobility')}
                >
                  <div
                    className={`flex items-center justify-between px-3.5 py-2.5 rounded-lg text-[13px] font-600 tracking-normal transition-colors cursor-pointer select-none ${
                      activeCategory === 'emobility'
                        ? 'bg-slate-100/80 text-emerald-700'
                        : 'text-slate-800 hover:text-emerald-700 hover:bg-slate-50'
                    }`}
                  >
                    <span>&gt; E-Mobility Solution</span>
                    <span className="text-[12px] text-slate-400">›</span>
                  </div>

                  {/* Submenu for E-Mobility Solution: Shows > Chargify CMS */}
                  <div
                    className={`absolute left-full top-0 pl-1.5 w-[200px] transition-all duration-150 ${
                      activeCategory === 'emobility'
                        ? 'opacity-100 pointer-events-auto block'
                        : 'opacity-0 pointer-events-none hidden group-hover/emobility:block group-hover/emobility:opacity-100 group-hover/emobility:pointer-events-auto'
                    }`}
                  >
                    <div className="rounded-xl border border-slate-200/80 bg-white/95 backdrop-blur-xl p-1.5 shadow-xl shadow-slate-900/10 flex flex-col gap-1">
                      <a
                        href="https://chargify.co.in"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => { setOpen(false); setActiveCategory(null); }}
                        className="px-3.5 py-2 rounded-lg text-[13px] font-600 tracking-normal text-slate-800 hover:text-emerald-700 hover:bg-slate-50 transition-colors block"
                      >
                        &gt; Chargify CMS
                      </a>
                    </div>
                  </div>
                </div>

                {/* Option 2: Smart Industrial Suite */}
                <div
                  className="relative group/industrial"
                  onMouseEnter={() => setActiveCategory('industrial')}
                >
                  <div
                    className={`flex items-center justify-between px-3.5 py-2.5 rounded-lg text-[13px] font-600 tracking-normal transition-colors cursor-pointer select-none ${
                      activeCategory === 'industrial'
                        ? 'bg-slate-100/80 text-cyan'
                        : 'text-slate-800 hover:text-cyan hover:bg-slate-50'
                    }`}
                  >
                    <span>&gt; Smart Industrial Suite</span>
                    <span className="text-[12px] text-slate-400">›</span>
                  </div>

                  {/* Submenu for Smart Industrial Suite: Shows > PMMS, > MMMS */}
                  <div
                    className={`absolute left-full top-0 pl-1.5 w-[180px] transition-all duration-150 ${
                      activeCategory === 'industrial'
                        ? 'opacity-100 pointer-events-auto block'
                        : 'opacity-0 pointer-events-none hidden group-hover/industrial:block group-hover/industrial:opacity-100 group-hover/industrial:pointer-events-auto'
                    }`}
                  >
                    <div className="rounded-xl border border-slate-200/80 bg-white/95 backdrop-blur-xl p-1.5 shadow-xl shadow-slate-900/10 flex flex-col gap-1">
                      <a
                        href="https://pmms.emberonlabs.in"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => { setOpen(false); setActiveCategory(null); }}
                        className="px-3.5 py-2 rounded-lg text-[13px] font-600 tracking-normal text-slate-800 hover:text-cyan hover:bg-slate-50 transition-colors block"
                      >
                        &gt; PMMS
                      </a>
                      <a
                        href="https://mmms.emberonlabs.in"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => { setOpen(false); setActiveCategory(null); }}
                        className="px-3.5 py-2 rounded-lg text-[13px] font-600 tracking-normal text-slate-800 hover:text-cyan hover:bg-slate-50 transition-colors block"
                      >
                        &gt; MMMS
                      </a>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button onClick={(e) => handleNavClick(e, '/#contact-section')}
             className="hidden sm:inline-flex items-center gap-2 text-[12.5px] font-700 tracking-[0.12em] text-white px-4 py-2.5 rounded-lg transition-transform hover:scale-[1.03] active:scale-95 shadow-md shadow-emerald-600/20"
             style={{background:'linear-gradient(100deg, var(--lead), var(--sec))'}}>
            {/* eslint-disable-next-line react/jsx-pascal-case */}
            CONTACT US <Ic.arrow className="w-4 h-4" />
          </button>
          <button onClick={() => setMobile((v) => !v)} className="lg:hidden grid place-items-center w-10 h-10 rounded-lg chip text-ink" aria-label="Menu">
            <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none"><path d={mobile ? 'M6 6l12 12M18 6 6 18' : 'M4 7h16M4 12h16M4 17h16'} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
          </button>
        </div>
      </nav>

      {/* Mobile Drawer */}
      <div className={`lg:hidden overflow-hidden transition-all duration-300 ${mobile ? 'max-h-[640px] border-b border-slate-200 shadow-lg' : 'max-h-0'}`}>
        <div className="bg-white/95 backdrop-blur-xl px-5 py-4 flex flex-col gap-1">
          {links.map((l) => (
            <Link key={l.label} to={l.href} onClick={(e) => { handleNavClick(e, l.href); setMobile(false); }}
               className="px-3 py-2.5 rounded-lg text-[13px] font-600 tracking-[0.12em] text-mute hover:text-ink hover:bg-slate-50">{l.label}</Link>
          ))}

          {/* Mobile Products Accordion */}
          <div className="border-t border-slate-100 pt-2 mt-1">
            <button
              onClick={() => setMobileProductsOpen((v) => !v)}
              className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-[13px] font-600 tracking-[0.12em] text-mute hover:text-ink hover:bg-slate-50"
            >
              <span>PRODUCTS</span>
              {/* eslint-disable-next-line react/jsx-pascal-case */}
              <Ic.chevron className={`w-3.5 h-3.5 transition-transform duration-200 ${mobileProductsOpen ? 'rotate-180' : ''}`} />
            </button>

            {mobileProductsOpen && (
              <div className="pl-3 pr-2 py-2 space-y-2 bg-slate-50/70 rounded-xl my-1 border border-slate-100">
                {/* 1. E-Mobility Solution */}
                <div>
                  <div className="px-2 py-1 text-[13px] font-600 text-slate-800">
                    &gt; E-Mobility Solution
                  </div>
                  <div className="pl-4 pt-1">
                    <a
                      href="https://chargify.co.in"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setMobile(false)}
                      className="block px-2 py-1 text-[12.5px] font-500 text-slate-600 hover:text-emerald-700"
                    >
                      &gt; Chargify CMS
                    </a>
                  </div>
                </div>

                {/* 2. Smart Industrial Suite */}
                <div className="border-t border-slate-200/60 pt-2">
                  <div className="px-2 py-1 text-[13px] font-600 text-slate-800">
                    &gt; Smart Industrial Suite
                  </div>
                  <div className="pl-4 pt-1 space-y-1">
                    <a
                      href="https://pmms.emberonlabs.in"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setMobile(false)}
                      className="block px-2 py-1 text-[12.5px] font-500 text-slate-600 hover:text-cyan"
                    >
                      &gt; PMMS
                    </a>
                    <a
                      href="https://mmms.emberonlabs.in"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setMobile(false)}
                      className="block px-2 py-1 text-[12.5px] font-500 text-slate-600 hover:text-cyan"
                    >
                      &gt; MMMS
                    </a>
                  </div>
                </div>
              </div>
            )}
          </div>

          <button onClick={(e) => { handleNavClick(e, '/#contact-section'); setMobile(false); }} className="mt-2 text-center text-[13px] font-700 tracking-[0.12em] text-white px-4 py-3 rounded-lg shadow-md shadow-emerald-600/20" style={{background:'linear-gradient(100deg, var(--lead), var(--sec))'}}>CONTACT US</button>
        </div>
      </div>
    </header>
  );
}
